import React, { useState, useRef, useEffect } from 'react';
import { resolveAirlineLogo } from '../../utils/logos';
import Select from '../shared/Select';
import SelectArrow from '../shared/SelectArrow';
import MediaQuery from 'react-responsive';
import { FormattedMessage } from 'react-intl';
import { isFirefox } from 'react-device-detect';

export default function ItinerarySelect(props) {
  const { current = {}, locale, length } = props;
  const { index = 0, direction = '' } = current;
  const translatedDirection =
    direction === 'outbound' ? (
      <FormattedMessage id="outbound" defaultMessage="Outbound" />
    ) : direction === 'inbound' ? (
      <FormattedMessage id="inbound" defaultMessage="Return" />
    ) : null;
  const ITINERARY_SHRINK_BARRIER = 650;

  const formatter = new Intl.NumberFormat(locale);
  const outOfText = `${formatter.format(index + 1)} / ${formatter.format(
    length
  )}`;

  // New design: custom dropdown
  if (props.useNewDesign) {
    return (
      <GenericItinerarySelectNew
        {...props}
        translatedDirection={translatedDirection}
        outOfText={outOfText}
      />
    );
  }

  // Legacy behavior: native select with responsive behavior
  return (
    <MediaQuery minWidth={ITINERARY_SHRINK_BARRIER / props.relativeWidthRatio}>
      {(matches) => {
        return matches ? (
          <Select
            translatedDirection={translatedDirection}
            ariaLabel="Select flight segment"
            {...props}
          >
            {props.children}
          </Select>
        ) : (
          GenericItinerarySelect(props, translatedDirection, outOfText)
        );
      }}
    </MediaQuery>
  );
}

function GenericItinerarySelect(props, translatedDirection, outOfText) {
  return (
    <div
      className={`gordian-select gordian-select-box gr-rounded-md gr-text-title gr-bg-white gr-w-3/6 gr-h-full gr-relative gr-border gr-text-sm`}
      id={props.id}
    >
      <select
        onChange={(event) => props.handleChange(event.target.value)}
        className={`gr-block gr-appearance-none gr-w-full gr-h-full gr-bg-white gr-font-normal gr-rounded-none gr-pl-6 gordian-select-field
            ${translatedDirection && props.value != null ? 'gr-pb-4' : ''}`}
        value={props.value}
        aria-label="Select flight segment"
        title="Select flight segment"
      >
        {props.children}
      </select>
      <div
        className={`gr-pointer-events-none gr-text-secondary-text gr-text-sm gr-absolute gr-inset-y-0 gr-pl-6 gr-py-1 gr-mt-10 ${
          isFirefox ? 'gr-ml-1' : ''
        }`}
      >
        {props.value != null ? translatedDirection : ''}
      </div>
      <div className="gr-pointer-events-none gr-absolute gr-inset-y-0 gr-right-0 gr-flex gr-items-center gr-px-2">
        <div className="gr-hidden sm:gr-block gr-text-xs  gr-text-secondary-text gr-mr-1 sm:gr-mr-2">
          {outOfText}
        </div>
        <SelectArrow />
      </div>
    </div>
  );
}

function GenericItinerarySelectNew({
  flights,
  value,
  handleChange,
  id,
  translatedDirection,
  outOfText,
  children,
  theme,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  if (flights && flights.length > 0) {
    const activeFlight =
      flights.find((f) => f.segment_id === value) || flights[0];
    const activeDepCode = activeFlight?.departure_airport || '';
    const activeArrCode = activeFlight?.arrival_airport || '';
    const activeHasRoute = activeDepCode && activeArrCode;
    const activeFallback =
      [activeFlight?.marketing_airline, activeFlight?.marketing_flight_number]
        .filter(Boolean)
        .join(' ') || 'Select flight';

    // theme.logos lets a partner swap in their own carrier artwork, or hide
    // logos entirely, without any change on our side.
    const activeLogo = resolveAirlineLogo(
      theme,
      activeFlight?.marketing_airline,
      activeFlight?.marketing_airline_logo
    );

    const activeDepCity = activeFlight?.departure_city_name || '';
    const activeArrCity = activeFlight?.arrival_city_name || '';
    const activeSubtitle =
      activeDepCity && activeArrCity
        ? `${activeDepCity} → ${activeArrCity}`
        : [
            activeFlight?.marketing_airline,
            activeFlight?.marketing_flight_number,
          ]
            .filter(Boolean)
            .join(' ');

    return (
      <div className="gr-relative gr-flex-shrink-0" ref={containerRef} id={id}>
        {/* Trigger — same padding/height as passenger chips */}
        <button
          onClick={() => setOpen((o) => !o)}
          className="gr-flex gr-items-center gr-rounded-lg gr-border gr-bg-white gr-transition-all gr-duration-150 gr-cursor-pointer hover:gr-shadow-md hover:gr-border-gray-400 gordian-select-card"
          style={{
            gap: '10px',
            paddingTop: '10px',
            paddingBottom: '10px',
            paddingLeft: '12px',
            paddingRight: '12px',
            borderColor: open ? '#111827' : '#E5E7EB',
          }}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Select flight segment"
        >
          {/* Logo container — mirrors the passenger avatar circle */}
          <div
            className="gr-flex-shrink-0 gr-rounded-lg gr-flex gr-items-center gr-justify-center gordian-logo-tile"
            style={{
              width: '34px',
              height: '34px',
              backgroundColor: '#F3F4F6',
            }}
          >
            {activeLogo ? (
              <img
                src={activeLogo}
                alt={activeFlight.marketing_airline || ''}
                style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <span className="gr-text-xs gr-font-bold gr-text-gray-500">
                {activeDepCode || '?'}
              </span>
            )}
          </div>
          {/* Two-line text — same structure as passenger name+seat */}
          <div
            className="gr-flex gr-flex-col gr-items-start"
            style={{ gap: '1px' }}
          >
            <span className="gr-text-sm gr-font-medium gr-whitespace-nowrap gr-text-gray-900">
              {activeHasRoute ? (
                <>
                  {activeDepCode}
                  <span style={{ opacity: 0.4, margin: '0 4px' }}>→</span>
                  {activeArrCode}
                </>
              ) : (
                activeFallback
              )}
            </span>
            {activeSubtitle && (
              <span
                className="gr-text-xs gr-whitespace-nowrap"
                style={{ color: '#9CA3AF' }}
              >
                {activeSubtitle}
              </span>
            )}
          </div>
          <svg
            width="13"
            height="13"
            viewBox="0 0 13 13"
            fill="none"
            style={{
              flexShrink: 0,
              transition: 'transform 0.15s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              color: '#9CA3AF',
            }}
          >
            <path
              d="M2 4.5l4.5 4.5 4.5-4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Dropdown */}
        {open && (
          <div
            className="gr-absolute gr-top-full gr-left-0 gr-mt-1 gr-bg-white gr-rounded-xl gr-border gr-border-gray-200 gr-shadow-lg gr-z-50 gr-overflow-hidden gordian-select-menu"
            style={{ minWidth: '100%' }}
            role="listbox"
            aria-label="Select flight segment"
          >
            {flights.map((flight, idx) => {
              const isActive = value === flight.segment_id;
              const depCode = flight.departure_airport || '';
              const arrCode = flight.arrival_airport || '';
              const depCity = flight.departure_city_name || '';
              const arrCity = flight.arrival_city_name || '';
              const hasRoute = depCode && arrCode;
              const flightNum =
                [flight.marketing_airline, flight.marketing_flight_number]
                  .filter(Boolean)
                  .join(' ') || `Flight ${idx + 1}`;
              const subtitle =
                depCity && arrCity ? `${depCity} → ${arrCity}` : flightNum;

              return (
                <button
                  key={flight.segment_id}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    if (flight.available) {
                      handleChange(flight.segment_id);
                      setOpen(false);
                    }
                  }}
                  disabled={!flight.available}
                  className={`gr-w-full gr-flex gr-items-center gr-gap-3 gr-text-left gr-transition-colors gr-duration-100 ${
                    isActive ? '' : 'hover:gr-bg-gray-50'
                  } ${!flight.available ? 'gr-opacity-40 gr-cursor-not-allowed' : 'gr-cursor-pointer'}`}
                  style={{
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    paddingLeft: '14px',
                    paddingRight: '14px',
                    backgroundColor: isActive ? '#111827' : undefined,
                  }}
                >
                  {/* Logo in a contained box — always visible regardless of row bg */}
                  <div
                    className="gr-flex-shrink-0 gr-rounded-lg gr-flex gr-items-center gr-justify-center gordian-logo-tile"
                    style={{
                      width: '34px',
                      height: '34px',
                      backgroundColor: isActive
                        ? 'rgba(255,255,255,0.15)'
                        : '#F3F4F6',
                    }}
                  >
                    {resolveAirlineLogo(
                      theme,
                      flight.marketing_airline,
                      flight.marketing_airline_logo
                    ) ? (
                      <img
                        src={resolveAirlineLogo(
                          theme,
                          flight.marketing_airline,
                          flight.marketing_airline_logo
                        )}
                        alt={flight.marketing_airline || ''}
                        style={{
                          width: '24px',
                          height: '24px',
                          objectFit: 'contain',
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <span
                        className="gr-text-xs gr-font-bold"
                        style={{
                          color: isActive ? 'rgba(255,255,255,0.7)' : '#6B7280',
                        }}
                      >
                        {depCode || '?'}
                      </span>
                    )}
                  </div>
                  <div
                    className="gr-flex gr-flex-col gr-items-start"
                    style={{ gap: '1px' }}
                  >
                    <span
                      className="gr-text-sm gr-font-medium gr-whitespace-nowrap"
                      style={{ color: isActive ? '#ffffff' : '#111827' }}
                    >
                      {hasRoute ? (
                        <>
                          {depCode}
                          <span style={{ opacity: 0.5, margin: '0 5px' }}>
                            →
                          </span>
                          {arrCode}
                        </>
                      ) : (
                        flightNum
                      )}
                    </span>
                    {hasRoute && (
                      <span
                        className="gr-text-xs gr-whitespace-nowrap"
                        style={{
                          color: isActive
                            ? 'rgba(255,255,255,0.55)'
                            : '#9CA3AF',
                        }}
                      >
                        {subtitle}
                      </span>
                    )}
                  </div>
                  {isActive && (
                    <svg
                      className="gr-ml-auto gr-flex-shrink-0"
                      width="15"
                      height="15"
                      viewBox="0 0 15 15"
                      fill="none"
                    >
                      <path
                        d="M2.5 7.5l3.5 3.5 6.5-6.5"
                        stroke="#ffffff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Fallback to native select when flights data not available
  return (
    <div
      className="gordian-select gordian-select-box gr-rounded-md gr-text-title gr-bg-white gr-w-3/6 gr-h-full gr-relative gr-border gr-text-sm"
      id={id}
    >
      <select
        onChange={(event) => handleChange(event.target.value)}
        className={`gr-block gr-appearance-none gr-w-full gr-h-full gr-bg-white gr-font-normal gr-rounded-none gr-pl-6 gordian-select-field
            ${translatedDirection && value != null ? 'gr-pb-4' : ''}`}
        value={value}
        aria-label="Select flight segment"
        title="Select flight segment"
      >
        {children}
      </select>
      <div
        className={`gr-pointer-events-none gr-text-secondary-text gr-text-sm gr-absolute gr-inset-y-0 gr-pl-6 gr-py-1 gr-mt-10 ${
          isFirefox ? 'gr-ml-1' : ''
        }`}
      >
        {value != null ? translatedDirection : ''}
      </div>
      <div className="gr-pointer-events-none gr-absolute gr-inset-y-0 gr-right-0 gr-flex gr-items-center gr-px-2">
        <div className="gr-hidden sm:gr-block gr-text-xs gr-text-secondary-text gr-mr-1 sm:gr-mr-2">
          {outOfText}
        </div>
        <SelectArrow />
      </div>
    </div>
  );
}
